import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, Banknote, FileDown, Send, Pencil, Building, FileCheck2, ShieldCheck, RefreshCw, Mail, BellRing, Undo2, FileBadge2, Settings2, Percent, SquareKanban } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EntityLogoUpload } from "../components/EntityLogoUpload";

function PrefsDialog() {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ third_reminder_auto: false, digest_enabled: true });
  const [digest, setDigest] = useState(null);
  useEffect(() => { if (open) { api.get("/prefs").then((r) => setPrefs(r.data)).catch(() => {}); setDigest(null); } }, [open]);
  const save = async (p) => {
    setPrefs(p);
    try { await api.put("/prefs", { third_reminder_auto: !!p.third_reminder_auto, digest_enabled: p.digest_enabled !== false }); toast.success(t("msg.saved")); } catch { toast.error(t("msg.error")); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="icon" title={t("inv.prefs")} data-testid="prefs-button"><Settings2 className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent data-testid="prefs-dialog">
        <DialogHeader><DialogTitle>{t("inv.prefs")}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <label className="flex items-start gap-2 cursor-pointer"><input type="checkbox" className="mt-1" checked={!!prefs.third_reminder_auto} onChange={(e) => save({ ...prefs, third_reminder_auto: e.target.checked })} data-testid="pref-third-checkbox" />{t("inv.prefThird")}</label>
          <label className="flex items-start gap-2 cursor-pointer"><input type="checkbox" className="mt-1" checked={prefs.digest_enabled !== false} onChange={(e) => save({ ...prefs, digest_enabled: e.target.checked })} data-testid="pref-digest-checkbox" />{t("inv.prefDigest")}</label>
          <Button variant="outline" className="w-full" onClick={() => api.get("/digest/preview").then((r) => setDigest(r.data.text)).catch(() => {})} data-testid="digest-preview-button">{t("inv.digestPreview")}</Button>
          {digest !== null && <pre className="whitespace-pre-wrap text-xs bg-muted/40 rounded-md p-3 max-h-64 overflow-y-auto" data-testid="digest-preview-text">{digest || t("inv.digestEmpty")}</pre>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const paymentLabel = (t, m) => ({ bonifico: t("inv.bonifico"), rid: t("inv.rid"), riba: t("inv.riba") }[m] || m);

export const statusBadge = (status, t) => {
  if (status === "paid") return <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">{t("inv.paid")}</Badge>;
  if (status === "partial") return <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">{t("inv.partial")}</Badge>;
  return <Badge variant="destructive">{t("inv.unpaid")}</Badge>;
};

export function InvoiceTable({ invoices, kind, onRefresh, entityId }) {
  const { t, fmt, currentEntity } = useApp();
  const isElectronic = kind === "sale" && currentEntity?.country === "IT";
  const [payInv, setPayInv] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));

  const downloadXml = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(inv.number || "fattura").replaceAll("/", "-")}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    }
  };

  const navigate = useNavigate();
  const emailInvoice = (inv) => navigate(`/emails?invoice=${inv.id}`);
  const today = new Date().toISOString().slice(0, 10);
  const createTask = async (inv) => {
    try {
      const r = await api.post("/tasks/from-invoice", { invoice_id: inv.id });
      toast.success(r.data.existing ? t("inv.taskExists") : t("inv.taskCreated"), { action: { label: "→", onClick: () => navigate("/tasks") } });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    }
  };
  const remindInvoice = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/remind`);
      toast.success(`${t("inv.remindOk")}: ${r.data.to}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  const sendSdi = async (inv, dryRun = false) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/send-sdi?dry_run=${dryRun}`);
      toast.success(dryRun ? t("inv.sdiValidated") : t("inv.sdiSent"));
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    }
  };

  const syncSdi = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/sync-sdi`);
      toast.success(`${t("inv.sdiSync")}: ${r.data.status || "OK"}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    }
  };

  const registerPayment = async () => {
    try {
      await api.post(`/invoices/${payInv.id}/payment`, { amount: parseFloat(payAmount), date: payDate });
      toast.success(t("msg.saved"));
      setPayInv(null);
      onRefresh();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("msg.deleted"));
      onRefresh();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const [ncInv, setNcInv] = useState(null);
  const [nc, setNc] = useState({ date: today, amount_net: "", description: "" });
  const [afInv, setAfInv] = useState(null);
  const [af, setAf] = useState({ td_type: "TD17", date: today, vat_rate: "22" });
  const isNormal = (inv) => !inv.doc_type || inv.doc_type === "TD01";
  const createNc = async () => {
    try {
      await api.post(`/invoices/${ncInv.id}/credit-note`, { date: nc.date, amount_net: nc.amount_net === "" ? null : parseFloat(nc.amount_net), description: nc.description });
      toast.success(t("inv.creditNoteOk")); setNcInv(null); onRefresh();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };
  const createAf = async () => {
    try {
      await api.post(`/invoices/${afInv.id}/self-invoice`, { td_type: af.td_type, date: af.date, vat_rate: parseFloat(af.vat_rate) || 22 });
      toast.success(t("inv.selfInvoiceOk")); setAfInv(null); onRefresh();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };
  const docBadge = (inv) => inv.doc_type && inv.doc_type !== "TD01" ? <Badge variant="outline" className="ml-1 text-[10px] text-violet-500 border-violet-500/40" data-testid={`doctype-${inv.id}`}>{inv.doc_type === "TD04" ? "NC" : inv.doc_type}{inv.ref_invoice_number ? ` → ${inv.ref_invoice_number}` : ""}</Badge> : null;
  const [whInv, setWhInv] = useState(null);
  const [wh, setWh] = useState({ rate: "20", base_pct: "100" });
  const saveWh = async () => {
    try {
      await api.post(`/invoices/${whInv.id}/withholding`, { rate: parseFloat(wh.rate) || 0, base_pct: parseFloat(wh.base_pct) || 100 });
      toast.success(t("wh.saved")); setWhInv(null); onRefresh();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("common.number")}</TableHead>
            <TableHead>{kind === "sale" ? t("inv.client") : t("inv.supplier")}</TableHead>
            <TableHead>{t("common.date")}</TableHead>
            <TableHead>{t("common.dueDate")}</TableHead>
            <TableHead className="text-right">{t("common.net")}</TableHead>
            <TableHead className="text-right">{t("common.vat")}</TableHead>
            <TableHead className="text-right">{t("common.total")}</TableHead>
            <TableHead>{t("common.status")}</TableHead>
            <TableHead>{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => (
            <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
              <TableCell className="font-mono text-xs">{inv.number}</TableCell>
              <TableCell className="font-medium">{inv.counterparty}</TableCell>
              <TableCell className="font-mono text-xs">{inv.date}</TableCell>
              <TableCell className="font-mono text-xs">{inv.due_date || "—"}</TableCell>
              <TableCell className="text-right font-mono">{fmt(inv.net, inv.currency)}</TableCell>
              <TableCell className="text-right font-mono">{fmt(inv.vat, inv.currency)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{fmt(inv.total, inv.currency)}</TableCell>
              <TableCell>
                {statusBadge(inv.status, t)}
                {inv.payment_method && <div className="text-[10px] text-muted-foreground mt-1">{paymentLabel(t, inv.payment_method)}</div>}
                {inv.sdi_status === "sent" && <Badge variant="outline" className="ml-1 text-[10px] text-emerald-500 border-emerald-500/40">SDI ✓</Badge>}
                {inv.sdi_status === "validated" && <Badge variant="outline" className="ml-1 text-[10px] text-amber-500 border-amber-500/40">SDI test ✓</Badge>}
                {inv.sdi_status === "error" && <Badge variant="outline" className="ml-1 text-[10px] text-red-500 border-red-500/40">SDI ✗</Badge>}
                {inv.sdi_outcome_status && <div className="text-[10px] text-muted-foreground mt-1">SDI: {inv.sdi_outcome_status}</div>}
                {docBadge(inv)}
                {inv.reminder_count > 0 && <Badge variant="outline" className={`ml-1 text-[10px] ${inv.reminder_count >= 3 ? "text-red-500 border-red-500/40" : "text-amber-500 border-amber-500/40"}`} data-testid={`reminders-${inv.id}`}>{t("inv.reminders")} {Math.min(inv.reminder_count, 3)}/3</Badge>}
                {inv.third_reminder_pending && <div className="text-[10px] text-red-500 mt-1" data-testid={`third-pending-${inv.id}`}>{t("inv.thirdPending")}</div>}
                {inv.credited_amount > 0 && <div className="text-[10px] text-muted-foreground mt-1">NC: −{fmt(inv.credited_amount)}</div>}
                {inv.self_invoice_number && <div className="text-[10px] text-muted-foreground mt-1">AF: {inv.self_invoice_number}</div>}
                {inv.withholding_amount > 0 && <div className="text-[10px] text-amber-600 mt-1" data-testid={`wh-${inv.id}`}>{t("wh.short")} {inv.withholding_rate}%: −{fmt(inv.withholding_amount)}{inv.withholding_paid ? " ✓F24" : ""}</div>}
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {kind === "purchase" && isNormal(inv) && !inv.paid_amount && (
                    <Button size="icon" variant="ghost" title={t("wh.title")} data-testid={`withholding-${inv.id}`}
                      onClick={() => { setWhInv(inv); setWh({ rate: String(inv.withholding_rate ?? 20), base_pct: String(inv.withholding_base_pct ?? 100) }); }}>
                      <Percent className="h-4 w-4 text-amber-600" />
                    </Button>
                  )}
                  {isNormal(inv) && inv.status !== "draft" && inv.status !== "paid" && inv.due_date && inv.due_date < today && (
                    <Button size="icon" variant="ghost" title={t("inv.createTask")} data-testid={`create-task-${inv.id}`} onClick={() => createTask(inv)}>
                      <SquareKanban className="h-4 w-4 text-emerald-500" />
                    </Button>
                  )}
                  {isNormal(inv) && inv.status !== "draft" && (
                    <Button size="icon" variant="ghost" title={t("inv.creditNote")} data-testid={`credit-note-${inv.id}`}
                      onClick={() => { setNcInv(inv); setNc({ date: today, amount_net: "", description: "" }); }}>
                      <Undo2 className="h-4 w-4 text-violet-500" />
                    </Button>
                  )}
                  {kind === "purchase" && isNormal(inv) && inv.status !== "draft" && !inv.self_invoice_number && currentEntity?.country === "IT" && (
                    <Button size="icon" variant="ghost" title={t("inv.selfInvoice")} data-testid={`self-invoice-${inv.id}`}
                      onClick={() => { setAfInv(inv); setAf({ td_type: "TD17", date: today, vat_rate: "22" }); }}>
                      <FileBadge2 className="h-4 w-4 text-violet-500" />
                    </Button>
                  )}
                  {(isElectronic || (inv.doc_type && inv.doc_type !== "TD01" && inv.doc_type !== "TD04" && currentEntity?.country === "IT")) && inv.status !== "draft" && (
                    <>
                      <Button size="icon" variant="ghost" title={t("inv.xml")} data-testid={`xml-invoice-${inv.id}`} onClick={() => downloadXml(inv)}>
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title={t("inv.sendEmail")} data-testid={`email-invoice-${inv.id}`} onClick={() => emailInvoice(inv)}>
                        <Mail className="h-4 w-4" />
                      </Button>
                      {inv.status !== "paid" && inv.due_date && inv.due_date < today && (
                        <Button size="icon" variant="ghost" title={t("inv.remind")} data-testid={`remind-invoice-${inv.id}`} onClick={() => remindInvoice(inv)}>
                          <BellRing className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" title={t("inv.sendSdi")} data-testid={`sdi-invoice-${inv.id}`} onClick={() => sendSdi(inv)}>
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title={t("inv.sdiValidated")} data-testid={`sdi-dryrun-${inv.id}`} onClick={() => sendSdi(inv, true)}>
                        <ShieldCheck className="h-4 w-4" />
                      </Button>
                      {inv.sdi_provider === "aruba_fe" && inv.sdi_status === "sent" && (
                        <Button size="icon" variant="ghost" title={t("inv.sdiSync")} data-testid={`sdi-sync-${inv.id}`} onClick={() => syncSdi(inv)}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                  {inv.status !== "paid" && isNormal(inv) && (
                    <Button size="icon" variant="ghost" data-testid={`pay-invoice-${inv.id}`}
                      onClick={() => { setPayInv(inv); setPayAmount(String(inv.total - (inv.paid_amount || 0))); }}>
                      <Banknote className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!whInv} onOpenChange={(o) => !o && setWhInv(null)}>
        <DialogContent className="max-w-sm" data-testid="withholding-dialog">
          <DialogHeader><DialogTitle>{t("wh.title")} — {whInv?.counterparty}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("wh.rate")} %</Label><Input type="number" value={wh.rate} onChange={(e) => setWh({ ...wh, rate: e.target.value })} data-testid="wh-rate-input" /></div>
              <div><Label>{t("wh.base")} %</Label><Input type="number" value={wh.base_pct} onChange={(e) => setWh({ ...wh, base_pct: e.target.value })} data-testid="wh-base-input" /></div>
            </div>
            <p className="text-xs text-muted-foreground">{t("wh.hint")} {whInv ? fmt(whInv.net * (parseFloat(wh.base_pct) || 0) / 100 * (parseFloat(wh.rate) || 0) / 100) : ""}</p>
            <Button className="w-full" onClick={saveWh} data-testid="wh-save-button">{t("common.save")}</Button>
            {whInv?.withholding_amount > 0 && <Button variant="ghost" className="w-full text-xs" onClick={() => { setWh({ rate: "0", base_pct: "100" }); setTimeout(saveWh, 0); }} data-testid="wh-remove-button">{t("wh.remove")}</Button>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ncInv} onOpenChange={(o) => !o && setNcInv(null)}>
        <DialogContent data-testid="credit-note-dialog">
          <DialogHeader><DialogTitle>{t("inv.creditNote")} — {ncInv?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("common.date")}</Label><Input type="date" value={nc.date} onChange={(e) => setNc({ ...nc, date: e.target.value })} data-testid="nc-date-input" /></div>
            <div><Label>{t("inv.creditNoteNet")} ({t("inv.creditNoteFull")}: {ncInv ? fmt(ncInv.net - (ncInv.credited_net || 0)) : ""})</Label>
              <Input type="number" step="0.01" value={nc.amount_net} placeholder={t("inv.creditNoteFull")} onChange={(e) => setNc({ ...nc, amount_net: e.target.value })} data-testid="nc-amount-input" /></div>
            <div><Label>{t("common.description")}</Label><Input value={nc.description} onChange={(e) => setNc({ ...nc, description: e.target.value })} data-testid="nc-description-input" /></div>
            <Button className="w-full" onClick={createNc} data-testid="nc-submit-button">{t("inv.creditNote")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!afInv} onOpenChange={(o) => !o && setAfInv(null)}>
        <DialogContent data-testid="self-invoice-dialog">
          <DialogHeader><DialogTitle>{t("inv.selfInvoice")} — {afInv?.counterparty}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("inv.selfType")}</Label>
              <Select value={af.td_type} onValueChange={(v) => setAf({ ...af, td_type: v })}>
                <SelectTrigger data-testid="af-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["TD16", "TD17", "TD18", "TD19"].map((x) => <SelectItem key={x} value={x} data-testid={`af-type-${x}`}>{t(`inv.td.${x}`)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("common.date")}</Label><Input type="date" value={af.date} onChange={(e) => setAf({ ...af, date: e.target.value })} data-testid="af-date-input" /></div>
              <div><Label>{t("ord.vatRate")} %</Label><Input type="number" value={af.vat_rate} onChange={(e) => setAf({ ...af, vat_rate: e.target.value })} data-testid="af-vat-input" /></div>
            </div>
            <p className="text-xs text-muted-foreground">{t("common.net")}: {afInv ? fmt(afInv.net) : ""} → IVA {afInv ? fmt(afInv.net * (parseFloat(af.vat_rate) || 0) / 100) : ""}</p>
            <Button className="w-full" onClick={createAf} data-testid="af-submit-button">{t("inv.selfInvoice")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payInv} onOpenChange={(o) => !o && setPayInv(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("inv.markPaid")} — {payInv?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("inv.paymentAmount")}</Label>
              <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="payment-amount-input" />
            </div>
            <div>
              <Label>{t("common.date")}</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date-input" />
            </div>
            <Button className="w-full" onClick={registerPayment} data-testid="payment-submit-button">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function InvoiceForm({ kind, entityId, onSaved, contacts }) {
  const { t, currentEntity } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    counterparty: "", date: new Date().toISOString().slice(0, 10), due_date: "",
    description: "", net: "", vat_rate: kind === "sale" ? 22 : 22,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.counterparty.trim()) {
      toast.error(kind === "sale" ? t("inv.client") : t("inv.supplier"));
      return;
    }
    try {
      await api.post("/invoices", {
        entity_id: entityId, kind,
        counterparty: form.counterparty, date: form.date,
        contact_id: (contacts.find((c) => c.name === form.counterparty) || {}).id || null,
        due_date: form.due_date || null, description: form.description,
        net: parseFloat(form.net) || 0, vat_rate: parseFloat(form.vat_rate) || 0,
        currency: currentEntity?.currency || "EUR",
      });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm({ ...form, counterparty: "", description: "", net: "" });
      onSaved();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`new-${kind}-invoice-button`}><Plus className="h-4 w-4 mr-2" />{t("inv.new")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.new")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{kind === "sale" ? t("inv.client") : t("inv.supplier")}</Label>
            <Input value={form.counterparty} onChange={(e) => set("counterparty", e.target.value)}
              list={`contacts-${kind}`} data-testid="invoice-counterparty-input" required />
            <datalist id={`contacts-${kind}`}>
              {contacts.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.date")}</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="invoice-date-input" />
            </div>
            <div>
              <Label>{t("common.dueDate")}</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-input" />
            </div>
          </div>
          <div>
            <Label>{t("common.description")}</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="invoice-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.net")}</Label>
              <Input type="number" step="0.01" value={form.net} onChange={(e) => set("net", e.target.value)} data-testid="invoice-net-input" />
            </div>
            <div>
              <Label>{t("inv.vatRate")}</Label>
              <Input type="number" step="0.01" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid="invoice-vatrate-input" />
            </div>
          </div>
          <Button className="w-full" onClick={submit} data-testid="invoice-submit-button">{t("common.save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EntitySettingsDialog({ entityId }) {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ seller_name: "", seller_vat: "", seller_tax_code: "", street: "", cap: "", city: "", province: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (open) api.get(`/entities/${entityId}/settings`).then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
  }, [open, entityId]);
  const save = async () => {
    try {
      await api.put(`/entities/${entityId}/settings`, form);
      toast.success(t("msg.saved"));
      setOpen(false);
    } catch { toast.error(t("msg.error")); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="entity-settings-button"><Building className="h-4 w-4 mr-2" />{t("set.fiscal")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("set.fiscal")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("set.sellerName")}</Label>
            <Input value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)} data-testid="seller-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("set.sellerVat")}</Label>
              <Input value={form.seller_vat} onChange={(e) => set("seller_vat", e.target.value)} data-testid="seller-vat-input" /></div>
            <div><Label>{t("set.taxCode")}</Label>
              <Input value={form.seller_tax_code} onChange={(e) => set("seller_tax_code", e.target.value)} data-testid="seller-taxcode-input" /></div>
          </div>
          <div><Label>{t("contacts.street")}</Label>
            <Input value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="Via Roma, 10" data-testid="seller-street-input" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>{t("contacts.cap")}</Label>
              <Input value={form.cap} onChange={(e) => set("cap", e.target.value)} data-testid="seller-cap-input" /></div>
            <div><Label>{t("contacts.city")}</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="seller-city-input" /></div>
            <div><Label>{t("contacts.province")}</Label>
              <Input value={form.province} onChange={(e) => set("province", e.target.value)} maxLength={2} data-testid="seller-province-input" /></div>
          </div>
          <Button className="w-full" onClick={save} data-testid="seller-save-button">{t("common.save")}</Button>
          <EntityLogoUpload entityId={entityId} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDraftDialog({ draft, onClose, onSaved }) {
  const { t, fmt } = useApp();
  const [counterparty, setCounterparty] = useState(draft.counterparty);
  const [date, setDate] = useState(draft.date);
  const [lines, setLines] = useState(
    draft.lines?.length ? draft.lines : [{ description: draft.description || "", quantity: 1, unit_price: draft.net || 0, vat_rate: 22 }]
  );
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const total = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0) * (1 + (parseFloat(l.vat_rate) || 0) / 100), 0);

  const save = async () => {
    try {
      await api.put(`/invoices/${draft.id}`, {
        counterparty, date,
        lines: lines.map((l) => ({ description: l.description, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0, vat_rate: parseFloat(l.vat_rate) || 0 })),
      });
      toast.success(t("msg.saved"));
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{t("inv.edit")} — {t("inv.draft")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("inv.client")}</Label>
              <Input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} data-testid="draft-counterparty-input" /></div>
            <div><Label>{t("common.date")}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="draft-date-input" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t("inv.lines")}</Label>
              <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, { description: "", quantity: 1, unit_price: 0, vat_rate: 22 }])} data-testid="draft-add-line">
                <Plus className="h-3 w-3 mr-1" />{t("inv.addLine")}
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`draft-line-${i}`}>
                  <Input className="col-span-5" placeholder={t("common.description")} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" placeholder={t("inv.qty")} value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" placeholder={t("inv.unitPrice")} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" placeholder="IVA %" value={l.vat_rate} onChange={(e) => setLine(i, "vat_rate", e.target.value)} />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} disabled={lines.length === 1}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-right font-mono font-semibold mt-2">{t("common.total")}: {fmt(total)}</div>
          </div>
          <Button className="w-full" onClick={save} data-testid="draft-save-button">{t("common.save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ draft, onClose, onSaved }) {
  const { t } = useApp();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(draft.due_date || "");
  const [method, setMethod] = useState("bonifico");
  const issue = async () => {
    try {
      await api.post(`/invoices/${draft.id}/issue`, { date, due_date: dueDate || null, payment_method: method });
      toast.success(t("msg.saved"));
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.issueTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("common.date")}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="issue-date-input" /></div>
          <div><Label>{t("common.dueDate")}</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="issue-due-input" /></div>
          <div><Label>{t("inv.paymentMethod")}</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="issue-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bonifico">{t("inv.bonifico")}</SelectItem>
                <SelectItem value="rid">{t("inv.rid")}</SelectItem>
                <SelectItem value="riba">{t("inv.riba")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={issue} data-testid="issue-submit-button">{t("inv.issue")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DraftsSection({ entityId, onChanged }) {
  const { t, fmt } = useApp();
  const [drafts, setDrafts] = useState([]);
  const [editDraft, setEditDraft] = useState(null);
  const [issueDraft, setIssueDraft] = useState(null);

  const load = useCallback(() => {
    api.get(`/invoices?entity_id=${entityId}&kind=sale`)
      .then((r) => setDrafts(r.data.filter((i) => i.status === "draft"))).catch(() => {});
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged(); };
  if (drafts.length === 0) return null;

  return (
    <Card data-testid="drafts-card">
      <CardContent className="p-0">
        <div className="px-4 pt-4 pb-2 text-sm font-semibold flex items-center gap-2">
          {t("inv.drafts")}<Badge variant="secondary">{drafts.length}</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("inv.client")}</TableHead>
              <TableHead>{t("common.date")}</TableHead>
              <TableHead>{t("common.description")}</TableHead>
              <TableHead className="text-right">{t("common.total")}</TableHead>
              <TableHead>{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drafts.map((d) => (
              <TableRow key={d.id} data-testid={`draft-row-${d.id}`}>
                <TableCell className="font-medium">{d.counterparty}</TableCell>
                <TableCell className="font-mono text-xs">{d.date}</TableCell>
                <TableCell className="text-sm">{d.description}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{fmt(d.total, d.currency)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" title={t("inv.edit")} data-testid={`edit-draft-${d.id}`} onClick={() => setEditDraft(d)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" title={t("inv.issue")} data-testid={`issue-draft-${d.id}`} onClick={() => setIssueDraft(d)}>
                      <FileCheck2 className="h-4 w-4 text-primary" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {editDraft && <EditDraftDialog draft={editDraft} onClose={() => setEditDraft(null)} onSaved={refresh} />}
      {issueDraft && <IssueDialog draft={issueDraft} onClose={() => setIssueDraft(null)} onSaved={refresh} />}
    </Card>
  );
}

export default function Sales() {
  const { t, entity, currentEntity } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = useCallback(() => {
    api.get(`/invoices?entity_id=${entity}&kind=sale`).then((r) => setInvoices(r.data.filter((i) => i.status !== "draft"))).catch(() => {});
    api.get(`/contacts?entity_id=${entity}`).then((r) => setContacts(r.data.filter((c) => c.type === "client"))).catch(() => {});
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("nav.sales")}</h1>
        <div className="flex gap-2">
          <PrefsDialog />
          {currentEntity?.country === "IT" && <EntitySettingsDialog entityId={entity} />}
          <InvoiceForm kind="sale" entityId={entity} onSaved={load} contacts={contacts} />
        </div>
      </div>
      {currentEntity?.country === "IT" && <DraftsSection entityId={entity} onChanged={load} />}
      <Card>
        <CardContent className="p-0">
          {invoices.length === 0
            ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div>
            : <InvoiceTable invoices={invoices} kind="sale" onRefresh={load} entityId={entity} />}
        </CardContent>
      </Card>
    </div>
  );
}
